import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  FEEDBACK_COMMENT_MAX,
  FEEDBACK_EMAIL_MAX,
  FEEDBACK_ENDPOINT,
  FEEDBACK_NAME_MAX,
  FEEDBACK_RATING_MAX,
} from "../constants";
import { Dialog } from "./Dialog";

interface Props {
  open: boolean;
  onClose: () => void;
}

type FeedbackType = "incident" | "idea" | "contact";
type Status = "idle" | "submitting" | "success" | "error";
type ErrorKind = "generic" | "invalid" | "network";

interface FieldErrors {
  name?: string;
  email?: string;
  comment?: string;
}

const TYPE_ICONS: Record<FeedbackType, string> = {
  incident: "bug_report",
  idea: "lightbulb",
  contact: "forum",
};

const TYPE_ORDER: FeedbackType[] = ["incident", "idea", "contact"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function ratingFace(rating: number): { icon: string; tone: string } {
  if (rating === 0) return { icon: "sentiment_neutral", tone: "text-text-muted/60" };
  if (rating <= 3) return { icon: "sentiment_very_dissatisfied", tone: "text-signal-error" };
  if (rating <= 5) return { icon: "sentiment_dissatisfied", tone: "text-signal-warning" };
  if (rating <= 7) return { icon: "sentiment_neutral", tone: "text-signal-info" };
  if (rating <= 9) return { icon: "sentiment_satisfied", tone: "text-signal-success" };
  return { icon: "sentiment_very_satisfied", tone: "text-signal-success" };
}

export function FeedbackDialog({ open, onClose }: Props) {
  const { t } = useTranslation(["feedback", "common"]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [type, setType] = useState<FeedbackType>("idea");
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<Status>("idle");
  const [errorKind, setErrorKind] = useState<ErrorKind>("generic");

  useEffect(() => {
    if (open) {
      setName("");
      setEmail("");
      setType("idea");
      setComment("");
      setRating(0);
      setErrors({});
      setStatus("idle");
      setErrorKind("generic");
    }
  }, [open]);

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = t("feedback:validation.required");
    if (!email.trim()) next.email = t("feedback:validation.required");
    else if (!EMAIL_RE.test(email)) next.email = t("feedback:validation.email");
    if (!comment.trim()) next.comment = t("feedback:validation.required");
    else if (comment.length > FEEDBACK_COMMENT_MAX)
      next.comment = t("feedback:validation.max", { max: FEEDBACK_COMMENT_MAX });
    return next;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === "submitting") return;

    const fieldErrors = validate();
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      setStatus("error");
      setErrorKind("invalid");
      return;
    }

    setStatus("submitting");
    setErrorKind("generic");

    const payload = {
      name: name.trim().slice(0, FEEDBACK_NAME_MAX),
      email: email.trim().slice(0, FEEDBACK_EMAIL_MAX),
      type,
      comment: comment.trim().slice(0, FEEDBACK_COMMENT_MAX),
      rating,
      client_locale: typeof navigator !== "undefined" ? navigator.language : "",
      client_timezone:
        typeof Intl !== "undefined"
          ? (Intl.DateTimeFormat().resolvedOptions().timeZone ?? "")
          : "",
    };

    try {
      const res = await fetch(FEEDBACK_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        setStatus("error");
        setErrorKind(res.status >= 500 ? "generic" : "invalid");
        return;
      }
      const body = (await res.json().catch(() => null)) as
        | { ok?: boolean }
        | null;
      if (body && body.ok === false) {
        setStatus("error");
        setErrorKind("generic");
        return;
      }
      setStatus("success");
    } catch {
      setStatus("error");
      setErrorKind("network");
    }
  };

  const submitting = status === "submitting";
  const success = status === "success";

  const face = useMemo(() => ratingFace(rating), [rating]);
  const commentRemaining = FEEDBACK_COMMENT_MAX - comment.length;

  const errorBannerVisible = status === "error" && errorKind !== "invalid";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      icon="rate_review"
      title={t("feedback:title")}
      width="rich"
      footer={
        success ? (
          <>
            <span />
            <button
              type="button"
              onClick={onClose}
              className="h-footer-btn px-3 inline-flex items-center rounded-sm text-12 font-bold uppercase tracking-widerx text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/20 transition-colors duration-150 focus-visible:outline-none focus-visible:border-brand-primary/60 focus-visible:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)] border border-transparent"
            >
              {t("feedback:buttons.ok")}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="h-footer-btn px-3 inline-flex items-center rounded-sm text-12 font-bold uppercase tracking-widerx text-text-muted hover:text-text-main hover:bg-surface-subtle transition-colors duration-150 disabled:opacity-50 focus-visible:outline-none focus-visible:border-brand-primary/60 focus-visible:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)] border border-transparent"
            >
              {t("feedback:buttons.cancel")}
            </button>
            <button
              type="submit"
              form="feedback-form"
              disabled={submitting}
              className="h-footer-btn px-3 inline-flex items-center gap-1 rounded-sm text-12 font-bold uppercase tracking-widerx text-brand-primary bg-brand-primary/10 hover:bg-brand-primary/20 transition-colors duration-150 disabled:cursor-wait disabled:opacity-60 focus-visible:outline-none focus-visible:border-brand-primary/60 focus-visible:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)] border border-transparent"
            >
              <span className="material-symbols-outlined text-[14px]">send</span>
              {submitting ? t("feedback:buttons.submitting") : t("feedback:buttons.submit")}
            </button>
          </>
        )
      }
    >
      {success ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <span
            className="material-symbols-outlined text-signal-success text-[44px]"
            aria-hidden="true"
          >
            check_circle
          </span>
          <p className="m-0 text-13 font-semibold text-text-main">
            {t("feedback:status.success.title")}
          </p>
          <p className="m-0 text-12 text-text-muted max-w-[400px]">
            {t("feedback:status.success.message")}
          </p>
        </div>
      ) : (
        <form
          id="feedback-form"
          onSubmit={handleSubmit}
          className="flex flex-col gap-3"
          noValidate
        >
          <p className="m-0 text-12 leading-[17px] text-text-muted">
            {t("feedback:description")}
          </p>

          <div className="grid gap-2 md:grid-cols-2">
            <Field
              id="feedback-name"
              label={t("feedback:fields.name.label")}
              error={errors.name}
            >
              <input
                id="feedback-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t("feedback:fields.name.placeholder")}
                autoComplete="name"
                maxLength={FEEDBACK_NAME_MAX}
                disabled={submitting}
                className={inputClass(!!errors.name)}
              />
            </Field>
            <Field
              id="feedback-email"
              label={t("feedback:fields.email.label")}
              error={errors.email}
            >
              <input
                id="feedback-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t("feedback:fields.email.placeholder")}
                autoComplete="email"
                maxLength={FEEDBACK_EMAIL_MAX}
                disabled={submitting}
                className={inputClass(!!errors.email)}
              />
            </Field>
          </div>

          <Field label={t("feedback:fields.type.label")}>
            <div role="radiogroup" aria-label={t("feedback:fields.type.label")} className="flex flex-wrap gap-1">
              {TYPE_ORDER.map(opt => {
                const active = type === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setType(opt)}
                    disabled={submitting}
                    className={[
                      "h-control px-2 inline-flex items-center gap-1 rounded-sm border text-12 font-bold uppercase tracking-widerx transition-colors duration-150",
                      "focus-visible:outline-none focus-visible:border-brand-primary/60 focus-visible:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)]",
                      "disabled:opacity-50",
                      active
                        ? "text-brand-primary bg-brand-primary/15 border-brand-primary/30"
                        : "text-text-muted bg-surface-subtle/50 border-border-subtle hover:text-brand-primary hover:border-brand-primary/60",
                    ].join(" ")}
                  >
                    <span className="material-symbols-outlined text-[14px]">{TYPE_ICONS[opt]}</span>
                    {t(`feedback:fields.type.options.${opt}`)}
                  </button>
                );
              })}
            </div>
          </Field>

          <Field
            id="feedback-comment"
            label={t("feedback:fields.comment.label")}
            error={errors.comment}
            helper={t("feedback:fields.comment.helper", { max: FEEDBACK_COMMENT_MAX })}
          >
            <textarea
              id="feedback-comment"
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder={t("feedback:fields.comment.placeholder")}
              rows={4}
              maxLength={FEEDBACK_COMMENT_MAX}
              disabled={submitting}
              className={textareaClass(!!errors.comment)}
            />
            <span className="font-mono text-10 text-text-muted/60 self-end">
              {commentRemaining}
            </span>
          </Field>

          <Field label={t("feedback:fields.rating.label")}>
            <div className="flex items-center gap-2">
              <span
                className={`material-symbols-outlined text-[24px] ${face.tone}`}
                aria-hidden="true"
              >
                {face.icon}
              </span>
              <input
                type="range"
                min={0}
                max={FEEDBACK_RATING_MAX}
                step={1}
                value={rating}
                onChange={e => setRating(Number(e.target.value))}
                aria-label={t("feedback:fields.rating.label")}
                disabled={submitting}
                className="flex-1 accent-brand-primary"
              />
              <span className="font-mono text-11 text-text-muted min-w-[5ch] text-right">
                {rating === 0
                  ? t("feedback:fields.rating.noneLabel")
                  : t("feedback:fields.rating.scale", { value: rating, max: FEEDBACK_RATING_MAX })}
              </span>
            </div>
          </Field>

          {errorBannerVisible && (
            <div
              role="alert"
              className="text-12 leading-[17px] border border-signal-error/40 bg-signal-error/10 text-signal-error rounded-sm px-2 py-1.5"
            >
              {t(`feedback:status.error.${errorKind}`)}
            </div>
          )}

          <p className="m-0 text-11 leading-4 text-text-muted/70">
            {t("feedback:privacy")}
          </p>
        </form>
      )}
    </Dialog>
  );
}

function inputClass(invalid: boolean): string {
  return [
    "w-full h-8 px-2 bg-surface-subtle/40 border rounded-sm text-13 text-text-main font-sans outline-none transition-colors duration-150",
    "placeholder:text-text-muted/70 disabled:opacity-50",
    invalid
      ? "border-signal-error/60 focus:border-signal-error/80 focus:shadow-[0_0_0_2px_rgb(var(--signal-error)/0.3)]"
      : "border-border-subtle focus:bg-surface-subtle/60 focus:border-brand-primary/60 focus:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)]",
  ].join(" ");
}

function textareaClass(invalid: boolean): string {
  return [
    "w-full p-2 bg-surface-subtle/40 border rounded-sm text-13 leading-[19px] text-text-main font-sans outline-none resize-y transition-colors duration-150",
    "placeholder:text-text-muted/70 disabled:opacity-50",
    invalid
      ? "border-signal-error/60 focus:border-signal-error/80 focus:shadow-[0_0_0_2px_rgb(var(--signal-error)/0.3)]"
      : "border-border-subtle focus:bg-surface-subtle/60 focus:border-brand-primary/60 focus:shadow-[0_0_0_2px_rgb(var(--brand-primary)/0.4)]",
  ].join(" ");
}

interface FieldProps {
  id?: string;
  label: string;
  error?: string;
  helper?: string;
  children: React.ReactNode;
}

function Field({ id, label, error, helper, children }: FieldProps) {
  return (
    <label htmlFor={id} className="flex flex-col gap-1">
      <span className="text-12 font-bold uppercase tracking-widerx text-text-muted">
        {label}
      </span>
      {children}
      {error ? (
        <span className="text-11 text-signal-error">{error}</span>
      ) : helper ? (
        <span className="text-11 text-text-muted/70">{helper}</span>
      ) : null}
    </label>
  );
}
